/**
 * The single timeline every entrance and exit is cut against.
 *
 * The splash used to animate its title on one schedule, fade its body on
 * another, and then wait out a hardcoded 1900ms that stood in for "the
 * animation is probably finished by now". Here everything is a numbered slot on
 * one staggered timeline, and the durations are published to CSS as custom
 * properties, so there is one source of truth for both what the browser plays
 * and how long the code waits for it.
 */

import type { CSSProperties } from "react";

/**
 * The word the splash prints, and the name it is erased down to.
 *
 * `litteratura` is Latin for writing; `litura` is Latin for a blot, a smear,
 * the erasure of something written. One is inside the other — strike `terat`
 * out of the middle and the word that survives is the name of what you did to
 * it. So the app's name is not illustrated by an erasure, it is produced by
 * one, and the run that goes is contiguous, the way an erasure poet strikes a
 * span rather than picking out letters.
 */
export const TITLE = "litteratura";
export const NAME = "litura";

/** Indices of `TITLE` that are rubbed out, in the order the hand crosses them. */
export const ERASED = [3, 4, 5, 6, 7] as const;

/** True when this letter of `TITLE` survives into `NAME`. */
export const survives = (index: number): boolean => !ERASED.includes(index as never);

/**
 * A letter's place among the survivors, which is the slot it leaves on.
 *
 * The farewell redacts the name the reader is looking at, not the word it was
 * cut from, so the stagger has to count the six letters still standing rather
 * than their positions in the eleven.
 */
export const survivorSlot = (index: number): number =>
  index - ERASED.filter((erased) => erased < index).length;

/** Gap between neighbouring slots on the way in. */
export const ENTER_STAGGER_MS = 50;
/** How long one slot takes to arrive. */
export const ENTER_MS = 560;

/** The beat the whole word is allowed to stand before the hand arrives. */
export const ERASE_HOLD_MS = 220;
/** Gap between neighbouring letters of the run, so the hand reads as travelling. */
export const ERASE_STAGGER_MS = 60;
/** How long one letter takes to come away under the rubber. */
export const RUB_MS = 620;
/** How long the survivors take to close the gap the run left. */
export const CLOSE_MS = 400;
/**
 * How much of the rub the closing overlaps.
 *
 * Waiting for a letter to finish vanishing before its space starts to close
 * reads as two separate events. Starting the close while the last of the ink is
 * still coming away reads as one continuous gesture.
 */
export const CLOSE_OVERLAP_MS = 160;

/** On the way out the cadence tightens: erasing a page is a decisive gesture. */
export const EXIT_STAGGER_MS = 45;
export const EXIT_MS = 340;

/** The editor's page arriving, and the erased words settling onto it after it. */
export const PAGE_IN_MS = 460;
export const MARK_MS = 260;
export const MARK_DELAY_MS = 320;

/**
 * Slots on the entrance timeline, picked up where the title's letters leave off
 * so the sweep carries on down the screen instead of restarting.
 */
export const ENTER_SLOTS = {
  form: TITLE.length,
  scan: TITLE.length + 1,
  library: TITLE.length + 2,
  footer: TITLE.length + 3,
} as const;

/**
 * Slots on the farewell timeline, bottom of the screen first: the page clears
 * upward and the title is the last thing left to redact.
 */
export const EXIT_SLOTS = {
  footer: 0,
  library: 1,
  scan: 2,
  form: 3,
} as const;

/**
 * A long library would otherwise take its last entry several seconds to arrive,
 * so the per-entry stagger stops after this many and the rest come in together.
 */
export const LIBRARY_STAGGER_LIMIT = 5;

/** True when the reader has asked their system for less animation. */
export const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** CSS `animation-delay` for a slot of the entrance. */
export const enterDelay = (slot: number): string =>
  prefersReducedMotion() ? "0ms" : `${slot * ENTER_STAGGER_MS}ms`;

/** CSS `animation-delay` for a slot of the farewell. */
export const exitDelay = (slot: number): string =>
  prefersReducedMotion() ? "0ms" : `${slot * EXIT_STAGGER_MS}ms`;

/** When the last letter of the whole printed word has finished arriving. */
export const titleLanded = (): number => (TITLE.length - 1) * ENTER_STAGGER_MS + ENTER_MS;

/**
 * CSS `animation-delay` for rubbing out one letter of the run.
 *
 * Measured from the moment the whole word is standing, so the reader reads
 * `litteratura` before anything happens to it. Without that beat the erasure is
 * something the title did on its way in rather than something done to it.
 */
export const eraseDelay = (slot: number): string =>
  prefersReducedMotion()
    ? "0ms"
    : `${titleLanded() + ERASE_HOLD_MS + slot * ERASE_STAGGER_MS}ms`;

/** CSS `animation-delay` for the space one rubbed-out letter leaves closing. */
export const closeDelay = (slot: number): string =>
  prefersReducedMotion()
    ? "0ms"
    : `${titleLanded() + ERASE_HOLD_MS + slot * ERASE_STAGGER_MS + RUB_MS - CLOSE_OVERLAP_MS}ms`;

/**
 * How long the title takes to finish becoming the name, for anything that wants
 * to know when the opening has settled.
 */
export const eraseDuration = (): number =>
  prefersReducedMotion()
    ? 0
    : titleLanded() +
      ERASE_HOLD_MS +
      (ERASED.length - 1) * ERASE_STAGGER_MS +
      RUB_MS -
      CLOSE_OVERLAP_MS +
      CLOSE_MS;

/**
 * How long the splash's farewell runs, so the editor can be mounted the moment
 * the last letter of the name goes back under its redaction bar — rather than
 * at some guessed time before or after it.
 */
export const exitDuration = (): number =>
  prefersReducedMotion() ? 0 : (NAME.length - 1) * EXIT_STAGGER_MS + EXIT_MS;

/** How long the editor's erased words take to settle in after it opens. */
export const settleDuration = (): number =>
  prefersReducedMotion() ? 0 : MARK_DELAY_MS + MARK_MS;

/** The toolbar follows the page in rather than arriving under it. */
export const toolbarDelay = (): string =>
  prefersReducedMotion() ? "0ms" : `${PAGE_IN_MS}ms`;

/** The delay marked words hold their unerased look for when the editor opens. */
export const markDelay = (): string =>
  prefersReducedMotion() ? "0ms" : `${MARK_DELAY_MS}ms`;

/**
 * The durations, published to CSS. `motion.css` reads these rather than
 * repeating the numbers, which is what keeps the promises above honest.
 */
export const motionVars = (): CSSProperties =>
  ({
    "--enter-ms": `${ENTER_MS}ms`,
    "--exit-ms": `${EXIT_MS}ms`,
    "--rub-ms": `${RUB_MS}ms`,
    "--close-ms": `${CLOSE_MS}ms`,
    "--page-in-ms": `${PAGE_IN_MS}ms`,
    "--mark-ms": `${MARK_MS}ms`,
  }) as CSSProperties;

/** Resolves after `ms`, so an animation can be awaited alongside real work. */
export const wait = (ms: number): Promise<void> =>
  ms <= 0 ? Promise.resolve() : new Promise((resolve) => setTimeout(resolve, ms));
